import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { AppHttpService } from '../../../../../../../../shared/services';
import { v1URL } from '../../../../../../../../shared/constants/urls.constants';
import { issueExportFileName } from '../../../../../../../issue/issue.service';
import { ProjectDetailsService } from '../../project-details.service';

export type ReportMapDataFormat = 'kmz' | 'geojson';

interface ErrorBody { message?: string; error?: string | { message?: string } }

export const REPORT_MAP_DATA_OPTIONS: { format: ReportMapDataFormat; label: string; icon: string }[] = [
  { format: 'kmz', label: 'KMZ Map File', icon: 'map' },
  { format: 'geojson', label: 'GeoJSON File', icon: 'data_object' },
];

/**
 * Daily reports tab: export menu for the map data (captured shapes / pins) of
 * the reports the filters show. Hidden on projects with no map data.
 */
@Component({
  selector: 'app-report-map-data-export',
  templateUrl: './report-map-data-export.component.html',
  styleUrls: ['./report-map-data-export.component.scss'],
  imports: [MatIconModule, MatMenuModule, MatProgressSpinnerModule, MatTooltipModule],
})
export class ReportMapDataExportComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string | null = null;
  @Input() projectName = '';

  readonly options = REPORT_MAP_DATA_OPTIONS;
  available = false;
  reportIds: string[] = [];
  exporting: ReportMapDataFormat | null = null;

  private readonly appHttpService = inject(AppHttpService);
  private readonly projectDetailsService = inject(ProjectDetailsService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly onDestroy = new Subject<void>();

  ngOnInit(): void {
    this.projectDetailsService.filteredDailyReportIds
      .pipe(takeUntil(this.onDestroy))
      .subscribe(ids => {
        this.reportIds = ids || [];
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.projectId && this.projectId) {
      this.available = false;
      this.appHttpService
        .getService(v1URL.project.reportMapData.available.url({ projectId: this.projectId }))
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: (resp: { available?: boolean }) => {
            this.available = !!resp?.available;
          },
          error: () => {
            this.available = false;
          },
        });
    }
  }

  export(format: ReportMapDataFormat): void {
    if (!this.projectId || this.exporting) {
      return;
    }
    if (!this.reportIds.length) {
      this.snackBar.open('No reports match the current filters', '', { duration: 5000 });
      return;
    }
    const fallbackName = `${this.projectName || 'Project'} - Map data.${format}`;
    this.exporting = format;
    this.appHttpService
      .postService(
        v1URL.project.reportMapData.export.url({ projectId: this.projectId, format }),
        { report_ids: this.reportIds },
        {},
        { responseType: 'blob', observe: 'response' },
      )
      .toPromise()
      .then((resp: HttpResponse<Blob> | null) => {
        if (!(resp?.body instanceof Blob)) {
          throw new Error('Export failed');
        }
        saveAs(resp.body, issueExportFileName(resp.headers?.get('Content-Disposition'), fallbackName));
        // Bid item entries / issues the export left out for having no location
        const skipped = Number(resp.headers?.get('X-Map-Data-Skipped')) || 0;
        if (skipped > 0) {
          this.snackBar.open(
            `${skipped} ${skipped === 1 ? 'entry has' : 'entries have'} no location and ${skipped === 1 ? 'isn\'t' : 'aren\'t'} in the file`,
            '',
            { duration: 6000 },
          );
        }
      }, async (err: { error?: unknown }) => {
        let message = 'Export failed';
        try {
          const body: ErrorBody = err?.error instanceof Blob ? JSON.parse(await err.error.text()) : (err?.error as ErrorBody);
          const nested = typeof body?.error === 'string' ? body.error : body?.error?.message;
          message = nested || body?.message || message;
        } catch { /* keep default */ }
        throw new Error(message);
      })
      .catch((err: Error) => {
        this.snackBar.open(err?.message || 'Export failed', '', { duration: 5000 });
      })
      .finally(() => {
        this.exporting = null;
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
